import React from 'react';
import { Box } from '@mui/material';
import CartLoader from '../../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { calculateDynamicRange, getTooltipHtmlForComparison } from '../../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import { applyHiddenSeries, EMPTY_HIDDEN_KEYS } from '../../../../../Helper/DigitalTwin/comparisonSeriesVisibility';
import BaseVitalChart from '../../../components/BaseVitalChart';
import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../../../Helper/DigitalTwin/VitalChartConfigurations';


const VitalsComparisonChart = ({
    series,
    categories,
    startDate,
    isLoading,
    primaryGranularity,
    currentMetric,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    chartKey,
    zoomResetViewRef,
    onZoom = () => {},
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false,
    // Legend keys switched off in the shared comparison legend (ComparisonChartSection).
    hiddenSeriesKeys = EMPTY_HIDDEN_KEYS

}) => {

    const { t } = useTranslation()
    const chartHeight = 237

    // What the chart draws: hidden series keep their slot with data: [] so the colour,
    // fill and stroke arrays (built per index) stay aligned with the visible ones.
    // `series` (the full set) is still used for the "no data" check and tooltip enable,
    // so hiding every series does not pretend the date has no data.
    const renderedSeries = React.useMemo(
        () => applyHiddenSeries(series || [], hiddenSeriesKeys),
        [series, hiddenSeriesKeys]
    )

    const getChartSettings = () => {
        const baseChartSettings = getBaseChartSettings(chartID, onZoom, setLoading)
        return { ...baseChartSettings, height: chartHeight, group: 'comparison' }
    }

    const getYAxisSettings = () => {
        let ranges = currentMetric.ranges
        // Range from the visible series only. calculateDynamicRange never narrows below
        // the metric's own min/max, and with every series hidden there is no data so the
        // metric range is used as-is - the axis never collapses or goes empty.
        if (renderedSeries && renderedSeries.length > 0) {
            // Combine all y values from all series into a single array
            // This is efficient even with 20-30 series as we only extract y values
            const combinedData = renderedSeries.reduce((acc, seriesItem) => {
                if (seriesItem && seriesItem.data && Array.isArray(seriesItem.data)) {
                    seriesItem.data.forEach(dataPoint => {
                        if (dataPoint && dataPoint.y !== null && dataPoint.y !== undefined) {
                            if (Array.isArray(dataPoint.y)) {
                                acc.push({ y: dataPoint.y[0] }, { y: dataPoint.y[1] });
                            } else {
                                acc.push({ y: dataPoint.y });
                            }
                        }
                    });
                }
                return acc;
            }, []);

            if (combinedData.length > 0) {
                ranges = calculateDynamicRange(combinedData, ranges.min, ranges.max)
            }
        }
        return {
            tickAmount: 5,
            position: 'top',
            min: ranges.min,
            max: ranges.max,
            axisTicks: {
                show: true,
                borderType: 'dotted'
            },

            title: {
                text: currentMetric.name,
                position: 'high',
                rotate: -90,
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#494949',
                },
            },
            labels: {
                show: true,
                offsetX: 0,
                offsetY: 0,
                style: {
                    colors: '#87939D',
                    fontSize: '10px',
                    fontWeight: 500,
                    fontfamily: 'Roboto !important'
                },
                formatter: (value) => Math.round(value),
            },
        }
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.enabled = series.length > 0
        tooltipSettings.followCursor = false
        tooltipSettings.custom = (opts) => getTooltipHtmlForComparison(opts, t, hiddenSeriesKeys)
        return tooltipSettings
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        const base = getBaseCanvasSettings(primaryGranularity, renderedSeries)
        const hasRangeArea = renderedSeries.some(s => s.type === 'rangeArea')
        if (!hasRangeArea) return base
        const fillOpacities = renderedSeries.map(s => s.type === 'rangeArea' ? 0.15 : 1)
        const strokeWidths = renderedSeries.map(s => s.type === 'rangeArea' ? 0 : 2)
        base.fill = { ...base.fill, opacity: fillOpacities }
        base.stroke = { ...base.stroke, width: strokeWidths }
        return base
    }

    const getChartOptions = () => {
        return {
            ...getCanvasSettings(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }

    // NEVER UPDATE A MOUNTED CHART IN PLACE. Every chart here is in the
    // 'comparison' group, and ApexCharts' updateOptions on a grouped chart
    // applies those options to EVERY chart in the group, and strips `series`
    // from them while doing so (UpdateHelpers._updateOptions). So one chart's
    // update overwrote its siblings' ids, axes and tooltips, and the chart that
    // asked for the update never received its own new series: the legend hid
    // nothing and the synced tooltip stopped appearing. react-apexcharts calls
    // updateOptions whenever the options object changes, so instead the options
    // are memoised and the chart is remounted (new key) whenever they do change.
    // A render that changes nothing keeps the same options and touches nothing.
    const chartOptions = React.useMemo(
        () => getChartOptions(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [renderedSeries, hiddenSeriesKeys, series, categories, startDate, primaryGranularity,
            currentMetric, zoomedRange, chartID, onZoom, setLoading, t]
    )
    const optionsVersion = React.useRef({ options: null, version: 0 })
    if (optionsVersion.current.options !== chartOptions) {
        optionsVersion.current = { options: chartOptions, version: optionsVersion.current.version + 1 }
    }

    if (isLoading) {
        return (
            <Box sx={{
                height: `${chartHeight}px`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <CartLoader />
            </Box>
        );
    }

    return (
        <BaseVitalChart
            options={chartOptions}
            series={renderedSeries}
            chartID={chartID}
            chartHeight={chartHeight}
            disableZoomReset={true}
            primaryGranularity={primaryGranularity}
            resetToLatestDataDate={resetToLatestDataDate}
            isDataSeriesEmpty={isDataSeriesEmpty}
            zoomResetViewRef={zoomResetViewRef}
            chartKey={`${chartKey}-${optionsVersion.current.version}`}
            chartProps={{
                width: '100%',
            }}
            onZoomReset={onZoomReset}
            isZoomed={isZoomed}
        />
    );
};

export default VitalsComparisonChart;
